import { isEqual, merge } from 'lodash';

import MerpsApiProvider from '../../../infrastructure/providers/pb-adapter/MerpsApiProvider';
import {
  CategoryOpenMode,
  CategoryUI,
} from '../../../models/category/category';
import { DocLinksMode } from '../../../models/docLinks/docLinks';
import CategoryService from '../../../services/category/CategoryService';
import IntlService from '../../../services/intl/IntlService';
import {
  getCategoryMasterDataset,
  getCategoryState,
} from '../../selectors/category';
import {
  getContentEntity,
  getLastUsedDisplayModeContentTab,
  hasDisplayModeContentTab,
} from '../../selectors/content';
import { getSessionApplication } from '../../selectors/session';
import category from '../../slices/category';
import content from '../../slices/content';
import dataset from '../../slices/dataset';
import { DictionaryEffectType } from '../../slices/dictionary';
import docLinks from '../../slices/docLinks';
import graph from '../../slices/graph';
import { apiCall, createAppListener } from '../listenerHelpers';

const docLinksGraphListener = createAppListener();

const apiProvider = new MerpsApiProvider();
const intlService = new IntlService();
const categoryService = new CategoryService();

docLinksGraphListener.startListening(
  apiCall(
    {
      request: graph.open,
      success: graph.openSuccess,
      failure: graph.openFailure,
    },
    async ({ categoryName, docId }) => {
      const result = await apiProvider.docLinks.getGraph(categoryName, docId);
      const commonMessages = await intlService.getCommon();

      return result.map(node => ({
        ...node,
        category: node.category,
        label: commonMessages[node.category] ?? node.category,
      }));
    }
  )
);

docLinksGraphListener.startListening(
  apiCall(
    {
      request: docLinks.open,
      success: docLinks.openSuccess,
      failure: docLinks.openFailure,
    },
    async ({ categoryName, docId, mode }, _state, dispatch) => {
      const openDisplayModeUIType: CategoryUI = {
        mode: CategoryOpenMode.DICTIONARY,
        effect: {
          type: DictionaryEffectType.DOC_LINKS,
          docId,
          categoryName,
          mode,
        },
        multiSelect: false,
        hideCheckbox: true,
      };

      dispatch(
        category.openDisplayMode({
          category: 'DocsLinks',
          displayMode: mode === DocLinksMode.IN ? 'input' : 'output',
          ui: openDisplayModeUIType,
          inParams: { selDocument: docId },
        })
      );
    }
  )
);

docLinksGraphListener.startListening(
  apiCall(
    {
      request: docLinks.openDocs,
      success: docLinks.openDocsSuccess,
      failure: docLinks.openDocsFailure,
    },
    async (
      { categoryName, docId, sourceCategoryName, mode },
      state,
      dispatch
    ) => {
      const commonMessages = await intlService.getCommon();
      const application = getSessionApplication(state);
      const displayMode = await categoryService.getDisplayMode(
        application,
        categoryName
      );

      const inParams = {
        linksGraph: {
          mode,
          category: sourceCategoryName,
          id: docId,
          title:
            (mode === DocLinksMode.IN ? ' 🡆 ' : ' 🡄 ') +
            (commonMessages[sourceCategoryName] ?? sourceCategoryName),
        },
      };

      if (!hasDisplayModeContentTab(state, categoryName, displayMode, true)) {
        dispatch(
          category.openDisplayMode({
            category: categoryName,
            displayMode,
            ui: { mode: CategoryOpenMode.CONTENT },
            inParams: merge(inParams, {
              linksGraph: { isIgnoreConditions: true },
            }),
          })
        );
      } else {
        const contentId = getLastUsedDisplayModeContentTab(
          state,
          categoryName,
          displayMode,
          true
        );
        const { content: categoryId } = getContentEntity(state, contentId);
        const { source } = getCategoryState(state, categoryId);

        if (
          !isEqual(
            source.inParams?.linksGraph,
            merge(inParams.linksGraph, {
              isIgnoreConditions:
                source.inParams?.linksGraph.isIgnoreConditions,
            })
          )
        ) {
          dispatch(
            category.updateInParams({
              categoryId,
              inParams: merge({}, source.inParams, inParams),
              replace: true,
            })
          );
          const rootDataset = getCategoryMasterDataset(state, categoryId);
          if (rootDataset)
            dispatch(dataset.update({ datasetId: rootDataset.id }));
        }

        dispatch(content.activate({ id: contentId }));
      }
    }
  )
);

export default docLinksGraphListener.middleware;
